import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useBridgeDataInterface } from './bridge-data-interface';
import type { CommerceComponentsDataInterfaceWithAuthentication } from './bridge-data-interface';

type CommerceBridgeContextValue = CommerceComponentsDataInterfaceWithAuthentication | null | undefined;

type CommerceBridgeContextState = {
  enabled: boolean;
  hasCommittedRef: React.MutableRefObject<boolean>;
  initialRenderValueRef: React.MutableRefObject<CommerceBridgeContextValue>;
  publishedValueRef: React.MutableRefObject<CommerceBridgeContextValue>;
  revision: number;
};

// Provides synchronous bridge data on Pass 1 while falling back to 2 passes if Pass 1 throws.
export const CommerceBridgeContext = createContext<CommerceBridgeContextState | null>(null);

const useIsomorphicLayoutEffect: typeof useLayoutEffect = (effect, deps) => {
  const hook = typeof window === 'undefined' ? useEffect : useLayoutEffect;
  return hook(effect, deps);
};

type CommerceBridgeProviderProps = {
  children: ReactNode;
  enabled?: boolean;
  resetKeys?: Array<unknown>;
};

type CommerceBridgeDataSourceProps = {
  hasCommittedRef: React.MutableRefObject<boolean>;
  initialRenderValueRef: React.MutableRefObject<CommerceBridgeContextValue>;
  publish: (value: CommerceBridgeContextValue) => void;
};

type CommerceBridgeErrorSignalProps = {
  publish: (value: CommerceBridgeContextValue) => void;
};

const CommerceBridgeDataSource: React.FC<CommerceBridgeDataSourceProps> = ({
  hasCommittedRef,
  initialRenderValueRef,
  publish,
}) => {
  const bridgeData = useBridgeDataInterface();
  if (!hasCommittedRef.current) {
    // Stores initialize unauthenticated/empty during SSR and hydration; auth/cart state arrives post-hydration.
    initialRenderValueRef.current = bridgeData;
  }

  useIsomorphicLayoutEffect(() => {
    publish(bridgeData);
  }, [bridgeData, publish]);

  useIsomorphicLayoutEffect(
    () => () => {
      publish(undefined);
    },
    [publish]
  );

  return null;
};

const CommerceBridgeErrorSignal: React.FC<CommerceBridgeErrorSignalProps> = ({ publish }) => {
  useIsomorphicLayoutEffect(() => {
    publish(null);
    return () => {
      publish(undefined);
    };
  }, [publish]);

  return null;
};

export const CommerceBridgeProvider: React.FC<CommerceBridgeProviderProps> = ({
  children,
  enabled = true,
  resetKeys,
}) => {
  const [revision, setRevision] = useState(0);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const hasCommittedRef = useRef(false);
  const initialRenderValueRef = useRef<CommerceBridgeContextValue>(undefined);
  const publishedValueRef = useRef<CommerceBridgeContextValue>(undefined);

  const publish = useCallback((value: CommerceBridgeContextValue) => {
    if (value === undefined) {
      hasCommittedRef.current = false;
      initialRenderValueRef.current = undefined;
      if (!Object.is(publishedValueRef.current, undefined)) {
        publishedValueRef.current = undefined;
        if (enabledRef.current) {
          setRevision((prev) => prev + 1);
        }
      }
      return;
    }
    if (!hasCommittedRef.current && value !== null && Object.is(initialRenderValueRef.current, value)) {
      hasCommittedRef.current = true;
      publishedValueRef.current = value;
      return;
    }
    hasCommittedRef.current = true;
    if (!Object.is(publishedValueRef.current, value)) {
      publishedValueRef.current = value;
      setRevision((prev) => prev + 1);
    }
  }, []);

  const contextState = useMemo<CommerceBridgeContextState>(
    () => ({
      enabled,
      hasCommittedRef,
      initialRenderValueRef,
      publishedValueRef,
      revision,
    }),
    [enabled, revision]
  );

  return (
    <CommerceBridgeContext.Provider value={contextState}>
      {enabled ? (
        <ErrorBoundary
          resetKeys={resetKeys}
          fallbackRender={({ error }) => {
            console.error('Commerce bridge provider error:', error);
            return <CommerceBridgeErrorSignal publish={publish} />;
          }}
        >
          <CommerceBridgeDataSource
            hasCommittedRef={hasCommittedRef}
            initialRenderValueRef={initialRenderValueRef}
            publish={publish}
          />
        </ErrorBoundary>
      ) : null}
      <React.Fragment key='commerce-bridge-content'>{children}</React.Fragment>
    </CommerceBridgeContext.Provider>
  );
};

export const useCommerceBridge = (): CommerceBridgeContextValue => {
  const contextState = useContext(CommerceBridgeContext);
  if (!contextState) {
    return null;
  }
  if (!contextState.enabled) {
    return undefined;
  }
  return contextState.hasCommittedRef.current
    ? contextState.publishedValueRef.current
    : contextState.initialRenderValueRef.current;
};
